/**
 * Селект-поповер (§2.3 «<EnumPopover>», §5.2, §5.3) — главный рабочий контрол.
 *
 * Решения спеки, которые видно в коде:
 *
 * • Фокус при открытом поповере остаётся на триггере, активная строка
 *   объявляется через `aria-activedescendant` (§13).
 * • Tab закрывает поповер и уводит фокус дальше штатным порядком (§2.3).
 * • Esc закрывает без правки, Enter выбирает активную строку, ↑↓ Home/End
 *   ходят по строкам.
 * • «Очистить» — последняя строка, а не ещё одно значение: пустое значение
 *   это задача, а не вариант выбора (П3).
 *
 * Компонент управляемый целиком: и значение, и факт открытости.
 */
import { useId, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { Popover } from './Popover'
import { IconCheck, IconChevronDown } from './icons'
import './Select.css'

export interface SelectOption {
  value: string
  label: string
  /**
   * Чип значения — ровно такой же, как в ячейке таблицы. Когда он есть,
   * подпись отдельно не рисуется: чип уже содержит текст, а два одинаковых
   * слова в строке читались как ошибка.
   */
  badge?: ReactNode
  /** Пояснение под строкой: что это значение значит. Пишется в справочнике. */
  hint?: string
  disabled?: boolean
}

export interface SelectProps {
  value: string | null
  options: SelectOption[]
  onChange: (value: string | null) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Доступное имя контрола: «Приоритет (тир)». */
  label: string
  /** Текст пустого значения в триггере. */
  emptyLabel?: string
  /** Подпись последней строки. Пустая строка убирает её. */
  clearLabel?: string
  /** Разрешено ли пустое значение: у тира — да, у обязательных полей — нет. */
  allowEmpty?: boolean
  /**
   * Последняя строка списка — выход в настройки самого справочника. Нужна
   * там, где значения заводят по ходу работы: увидел, что нужного варианта
   * нет, и завёл его, не выискивая раздел в меню.
   */
  manage?: ReactNode
  /** m — поле формы 32 px, cell — ячейка таблицы 28 px. */
  size?: 'm' | 'cell'
  /** plain — без рамки: рамку в таблице рисует курсор ячейки. */
  variant?: 'field' | 'plain'
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
}

/**
 * Ширина списка: не уже ячейки и не уже 200 px, дальше — по содержимому.
 * Жёсткая ширина по ячейке обрезала длинные значения: «Не продвигать
 * (чек-лист)» в колонке шириной 160 px превращалось в «Не продвигать (чек-».
 */
const LIST_MIN_WIDTH = 200
const LIST_MAX_WIDTH = 420

export function Select({
  value,
  options,
  onChange,
  open,
  onOpenChange,
  label,
  emptyLabel = '— не задано',
  clearLabel = 'Очистить',
  allowEmpty = true,
  manage,
  size = 'm',
  variant = 'field',
  disabled = false,
  invalid = false,
  id,
  className,
}: SelectProps) {
  const reactId = useId()
  const baseId = id ?? `select${reactId}`
  /*
   * Триггер держим в состоянии, а не в ref: поповер может открыться в том же
   * рендере, в котором компонент только монтируется (редактор ячейки таблицы
   * открывается сразу). ref.current в этот момент ещё пуст, а повторного
   * рендера не будет — поповер так и остался бы без якоря и без позиции.
   */
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const selectedIndex = options.findIndex((option) => option.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined
  const optionId = (index: number) => `${baseId}-option-${index}`

  function openList() {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
    onOpenChange(true)
  }

  function closeList() {
    onOpenChange(false)
  }

  function commit(next: string | null) {
    onChange(next)
    closeList()
  }

  /** Следующая доступная строка в заданном направлении, с остановкой на краях. */
  function step(from: number, direction: 1 | -1): number {
    let index = from
    for (let i = 0; i < options.length; i += 1) {
      index += direction
      if (index < 0) index = 0
      if (index > options.length - 1) index = options.length - 1
      if (!options[index]?.disabled) return index
      if (index === 0 || index === options.length - 1) break
    }
    return from
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return

    if (!open) {
      /* Alt+↓ — явный аналог Enter для тех, кто ищет «выпадашку» (§5.3). */
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault()
        openList()
      }
      return
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        closeList()
        return
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((index) => step(index, 1))
        return
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((index) => step(index, -1))
        return
      case 'Home':
        event.preventDefault()
        setActiveIndex(step(-1, 1))
        return
      case 'End':
        event.preventDefault()
        setActiveIndex(step(options.length, -1))
        return
      case 'Enter': {
        event.preventDefault()
        const option = options[activeIndex]
        if (option && !option.disabled) commit(option.value)
        return
      }
      case 'Tab':
        /* Закрываем, но не мешаем: фокус уходит дальше штатным Tab (§2.3). */
        closeList()
        return
      default:
        break
    }
  }

  const classes = ['select', `select--${size}`]
  if (variant === 'plain') classes.push('select--plain')
  if (invalid) classes.push('select--invalid')
  if (className) classes.push(className)

  return (
    <div className={classes.join(' ')}>
      <button
        ref={setTrigger}
        type="button"
        id={baseId}
        className="select__trigger t-cell"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${baseId}-list`}
        aria-activedescendant={open ? optionId(activeIndex) : undefined}
        aria-label={label}
        aria-invalid={invalid || undefined}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return
          if (open) closeList()
          else openList()
        }}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? 'select__value t-truncate' : 'select__value select__value--empty'}>
          {selected ? (selected.badge ?? selected.label) : emptyLabel}
        </span>
        <IconChevronDown size={16} className="select__chevron" />
      </button>

      <Popover
        open={open && !disabled}
        anchor={trigger}
        onClose={closeList}
        role="none"
        minWidth={Math.max(trigger?.offsetWidth ?? 0, LIST_MIN_WIDTH)}
        maxWidth={LIST_MAX_WIDTH}
        labelledBy={baseId}
      >
        <div className="select__list" role="listbox" id={`${baseId}-list`} aria-label={label}>
          {options.map((option, index) => {
            const isSelected = option.value === value
            const isActive = index === activeIndex
            const optionClasses = ['select__option', 't-cell']
            if (isActive) optionClasses.push('select__option--active')

            return (
              <button
                key={option.value}
                type="button"
                id={optionId(index)}
                role="option"
                /* Фокус живёт на триггере: строки из tab-порядка убраны (§13). */
                tabIndex={-1}
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                className={optionClasses.join(' ')}
                /* Мышь не должна отбирать фокус у триггера: активная строка
                   едет за курсором, фокус остаётся на combobox. */
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (option.disabled) return
                  commit(option.value)
                }}
              >
                <span className="select__check">
                  {isSelected ? <IconCheck size={16} /> : null}
                </span>
                <span className="select__text">
                  {option.badge
                    ? <span className="select__badge">{option.badge}</span>
                    : <span className="select__label t-truncate">{option.label}</span>}
                  {option.hint ? <span className="select__hint t-meta">{option.hint}</span> : null}
                </span>
              </button>
            )
          })}

          {allowEmpty && clearLabel ? (
            <button
              type="button"
              className="select__clear t-cell"
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commit(null)}
            >
              {clearLabel}
            </button>
          ) : null}

          {manage ? <div className="select__manage">{manage}</div> : null}
        </div>
      </Popover>
    </div>
  )
}
